import type React from "react";
import {ButtonBase, Stack, Tooltip, Typography} from "@mui/material";
import {IconWrapper} from "~/components/IconWrapper/IconWrapper";
import {supplyTypeStyle} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/supplyTypeStyles";

type ChainSupplyTypeFilterProps = {
    /** Supply type codes present in the chain, with how many counterparties carry each. */
    options: Array<{code: string; count: number}>;
    /** `null` = All. */
    value: string | null;
    onChange: (value: string | null) => void;
};

/**
 * The design's `svc-cats` segmented control: "All" plus one icon per supply type, in that type's
 * `IconWrapper` colour once picked. Picking a type keeps only its counterparties; picking it again
 * goes back to All.
 */
export const ChainSupplyTypeFilter: React.FC<ChainSupplyTypeFilterProps> = ({options, value, onChange}) => {
    return (
        <Stack
            direction="row"
            alignItems="center"
            gap="2px"
            role="group"
            aria-label="Filter by supply type"
            sx={{p: '2px', bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.100', borderRadius: '8px'}}
        >
            <SegmentButton selected={value === null} onClick={() => onChange(null)} label="All">
                <Typography fontSize={12.5} fontWeight={600} color="inherit">All</Typography>
            </SegmentButton>
            {options.map(({code, count}) => {
                const {Icon, color, label} = supplyTypeStyle(code);
                const selected = value === code;
                return (
                    <SegmentButton
                        key={code}
                        selected={selected}
                        onClick={() => onChange(selected ? null : code)}
                        label={`${label} · ${count}`}
                    >
                        <IconWrapper size="small" color={selected ? color : 'default'} bg={false}>
                            <Icon/>
                        </IconWrapper>
                    </SegmentButton>
                );
            })}
        </Stack>
    );
};

type SegmentButtonProps = {
    selected: boolean;
    onClick: () => void;
    label: string;
    children: React.ReactNode;
};

function SegmentButton({selected, onClick, label, children}: SegmentButtonProps) {
    return (
        <Tooltip title={label}>
            <ButtonBase
                onClick={onClick}
                aria-pressed={selected}
                aria-label={label}
                sx={{
                    minWidth: 34,
                    height: 30,
                    px: 1,
                    borderRadius: '8px',
                    color: selected ? 'primary.main' : 'text.secondary',
                    bgcolor: selected ? 'common.white' : 'transparent',
                    boxShadow: selected ? '0 1px 2px rgba(0, 0, 0, 0.08)' : 'none',
                    '&:hover': {color: 'text.primary', bgcolor: selected ? 'common.white' : 'grey.100'},
                }}
            >
                {children}
            </ButtonBase>
        </Tooltip>
    );
}
